<template>
  <div class="max-w-4xl pb-48">
    <p class="mb-7 mt-16 text-md font-medium">{{ $t('personal_data') }}</p>
    <Form
      key="form"
      ref="form"
      v-slot="{ errors }"
      novalidate
      class="grid grid-cols-12 gap-3 text-blue-dark-900 sm:gap-4 lg:gap-7"
      @submit="onSubmit"
    >
      <template v-for="field in formFields">
        <InputField
          v-if="field.type === 'text' || field.type === 'email'"
          :key="field.handle"
          :label="field.label"
          :field="field"
          :rules="field.rules"
          :error="errors[field.handle]"
        />

        <AppSelect
          v-if="field.type === 'select'"
          :key="field.handle"
          :field="field"
          :options="field.options"
          :rules="field.rules"
          :placeholder="$t('salutation')"
          :error="errors[field.handle]"
        />

        <div
          v-if="field.handle === 'salutation'"
          :key="`${field.handle}-spacer`"
          class="col-span-12 md:col-span-6 lg:col-span-8"
        />
      </template>

      <!-- NEWSLETTER -->
      <div class="col-span-12">
        <p class="mt-12 text-lg font-medium">
          {{ $t('subscribe_to_newsletter') }}
        </p>

        <div class="mt-2 space-y-2">
          <Checkbox
            :model-value="subscribeToNewsletter"
            class="sm:text-md"
            @update:model-value="subscribeToNewsletter = $event.checked"
          >
            Newsletter
          </Checkbox>
          <Checkbox
            :model-value="subscribeToMediaMessages"
            class="sm:text-md"
            @update:model-value="subscribeToMediaMessages = $event.checked"
          >
            {{ $t('news') }}
          </Checkbox>
        </div>
      </div>

      <!-- PRIVACY POLICY -->
      <div class="relative col-span-12 pt-12 md:col-span-6 lg:col-span-12">
        <Field
          v-slot="{ field, errorMessage }"
          name="privacy_policy_accepted"
          :label="$t('privacy_policy_accepted')"
          rules="required"
          type="checkbox"
          :value="true"
          class="relative border"
        >
          <Checkbox
            :model-value="field.checked"
            class="text-base sm:text-lg"
            @update:model-value="field.onChange"
          >
            <span
              class="text-link"
              v-html="
                component_newsletter_defaults.text_checkbox_privacy_policy
              "
            />
          </Checkbox>
          <transition name="fade">
            <p
              v-if="errorMessage"
              class="pt-1 text-sm text-red has-bg-img:absolute has-bg-img:bottom-2.5 has-bg-img:line-clamp-1 has-bg-img:px-5"
            >
              {{ errorMessage }}
            </p>
          </transition>
        </Field>
      </div>

      <Transition mode="out-in">
        <div v-if="state !== 'success'" class="col-span-12">
          <AppButton :is-loading="loading">{{ $t('send') }}</AppButton>
        </div>

        <AlertBanner
          v-else-if="state === 'success'"
          class="col-span-12 mt-12"
          type="success"
        >
          <div
            v-if="state === 'success' && data.success_message"
            class="text-sm md:text-base"
            v-html="data.success_message"
          />
        </AlertBanner>
      </Transition>

      <transition>
        <AlertBanner v-if="state === 'error'" class="col-span-12" type="error">
          <template #error>
            <p v-if="state === 'error'" class="text-sm md:text-base">
              {{ submitErrorMessage }}
            </p>
          </template>
        </AlertBanner>
      </transition>

      <slot :errors="errors" />
    </Form>
  </div>
</template>

<script setup>
import { Form, Field } from 'vee-validate'
const { setUser } = useAuthStore()
const { t, locale } = useI18n()

defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const state = ref('idle')
const loading = ref(false)
const { component_newsletter_defaults } = useGlobalsStore()
const formFields = ref([
  {
    label: t('salutation'),
    handle: 'salutation',
    type: 'select',
    rules: 'required',
    width: 33,
    options: [
      {
        value: 'male',
        label: t('male'),
      },
      {
        value: 'female',
        label: t('female'),
      },
      {
        value: 'other',
        label: t('other'),
      },
    ],
  },
  {
    label: t('first_name'),
    handle: 'first_name',
    type: 'text',
    rules: 'required',
    width: 50,
  },
  {
    label: t('last_name'),
    handle: 'last_name',
    type: 'text',
    rules: 'required',
    width: 50,
  },
  {
    label: t('email'),
    handle: 'email',
    type: 'email',
    rules: 'required|email',
    width: 50,
  },
  {
    label: t('company'),
    handle: 'company',
    type: 'text',
    rules: 'required',
    width: 50,
  },
  {
    label: t('job_title'),
    handle: 'job_title',
    type: 'text',
    rules: '',
    width: 50,
  },
  {
    label: t('language'),
    handle: 'language',
    type: 'select',
    rules: 'required',
    width: 50,
    options: [
      {
        value: 'de',
        label: t('german'),
      },
      {
        value: 'fr',
        label: t('french'),
      },
    ],
  },
  {
    label: t('zip_code'),
    handle: 'zip_code',
    type: 'text',
    rules: 'required|numeric',
    width: 33,
  },
  {
    label: t('location'),
    handle: 'location',
    type: 'text',
    rules: 'required',
    width: 33,
  },
  {
    label: t('country'),
    handle: 'country',
    type: 'select',
    rules: 'required',
    width: 33,
    options: [
      {
        value: 'ch',
        label: t('countries.ch'),
      },
      {
        value: 'de',
        label: t('countries.de'),
      },
      {
        value: 'fr',
        label: t('countries.fr'),
      },
      {
        value: 'it',
        label: t('countries.it'),
      },
      {
        value: 'at',
        label: t('countries.at'),
      },
    ],
  },
  {
    label: t('phone_work'),
    handle: 'phone_work',
    type: 'text',
    rules: 'required',
    width: 50,
  },
  {
    label: t('phone_mobile'),
    handle: 'phone_mobile',
    type: 'text',
    rules: '',
    width: 50,
  },
])
const subscribeToNewsletter = ref(false)
const subscribeToMediaMessages = ref(false)
const submitErrorMessage = ref(null)

async function onSubmit(formData) {
  formData.subscribe_to_newsletter = subscribeToNewsletter.value
  formData.subscribe_to_media_messages = subscribeToMediaMessages.value

  try {
    loading.value = true
    state.value = 'idle'

    // Set user to null to prevent having the wrong user in the store
    setUser(null)

    await api(
      '/register',
      {
        method: 'POST',
        body: formData,
      },
      locale.value
    )

    state.value = 'success'
    loading.value = false
  } catch (e) {
    state.value = 'error'
    loading.value = false
    console.error(e)

    submitErrorMessage.value = e.response?._data?.message
    console.error(submitErrorMessage.value)
  }
}
</script>
